import { useTranslation } from 'react-i18next';

import type { CommunityProfile } from '~/api/community/types';
import type { MutuallyExclusive } from '~/types/utils';

import { useCommunityProfile } from '~/api/community';
import { HttpNotFoundError } from '~/api/errors';
import { Avatar, Dialog, ErrorState, LegacyRichTextRenderer, Separator, Tag, Text } from '~/components';
import { useIntl } from '~/hooks/useIntl';
import { getIllustrationByActivityId } from '~/utils/user';

import { getCommunityProfileNames } from '../community.utils';
import { CommunityProfileFooter } from '../CommunityProfileFooter/CommunityProfileFooter';
import {
  activitiesStyle,
  activityStyle,
  bodyStyle,
  brandStyle,
  contentStyle,
  descriptionStyle,
  fullNameStyle,
  headerStyle,
  headerWrapperStyle,
  openToWorkStyle,
  profileDisabledStyle,
  profilePictureStyle,
  regionsStyle,
  textWrapperStyle,
} from './CommunityProfileDialog.css';
import { CommunityProfileDialogSkeleton } from './CommunityProfileDialog.skeleton';

type Props = MutuallyExclusive<{ communityProfileId: number }, { communityProfile: CommunityProfile }>;

export const CommunityProfileDialog = ({ communityProfile: communityProfileProp, communityProfileId }: Props) => {
  const { data, error, isError, refetch } = useCommunityProfile(communityProfileId ?? null);

  const { formatConjunctiveList } = useIntl();
  const { t } = useTranslation(['common', 'community']);

  const communityProfile = communityProfileProp ?? data;

  if (isError) {
    return (
      <Dialog.Body>
        <Dialog.Header />
        {error instanceof HttpNotFoundError ? (
          <Text className={profileDisabledStyle}>{t('community:detail.disabled')}</Text>
        ) : (
          <ErrorState onRetry={refetch} title={t('community:detail.error.title')} />
        )}
      </Dialog.Body>
    );
  }

  if (!communityProfile) return <CommunityProfileDialogSkeleton />;

  const { fullName, name } = getCommunityProfileNames(communityProfile);

  return (
    <Dialog.Body className={bodyStyle}>
      <Dialog.Header className={headerStyle}>
        <div className={headerWrapperStyle}>
          <Avatar
            aria-label={t('community:avatar', { name })}
            className={profilePictureStyle}
            fallback={name.charAt(0)}
            src={communityProfile.profilePictureUrl ?? undefined}
          />

          <div className={textWrapperStyle}>
            <div>
              <p className={brandStyle}>{name}</p>
              {fullName && <p className={fullNameStyle}>{fullName}</p>}
            </div>

            {communityProfile.regions.length > 0 && (
              <p className={regionsStyle}>
                {t('community:profileCard.activeInRegion', {
                  regions: formatConjunctiveList(communityProfile.regions.map((region) => t(`community:regions.${region}`))),
                })}
              </p>
            )}

            {communityProfile.openToWork && <span className={openToWorkStyle}>{t('community:profileCard.openToWork')}</span>}

            <ul className={activitiesStyle}>
              {communityProfile.activities.map((activity) => (
                <li key={activity.id}>
                  <Tag
                    className={activityStyle}
                    illustration={getIllustrationByActivityId(activity.id)}
                    text={t(`common:activities.${activity.id}.label`)}
                  />
                </li>
              ))}
            </ul>
          </div>
        </div>
      </Dialog.Header>

      <div className={contentStyle}>
        {communityProfile.description && (
          <>
            <div className={descriptionStyle}>
              <LegacyRichTextRenderer content={communityProfile.description} />
            </div>

            <Separator aria-hidden />
          </>
        )}

        <CommunityProfileFooter profile={communityProfile} />
      </div>
    </Dialog.Body>
  );
};
